import BPIcon, { icons } from 'components/BionicUI/icons/BPIcon';
import { Tabs } from 'components/BionicUI/ui/tabs';
import { cn } from 'lib/utils';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { CardTab, TabsButtons, TabsContents } from './CardTabs';
import CardTitle, { SubtitleHighlight } from './CardTitle';
import { Columns, Rows, getColSpan, getRowSpan } from './cardUtils';
import CardTitleBar from './CardTitleBar';
import { useIntersectionObserver } from '@react-hooks-library/core';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';

interface CardProps {
  children?: ReactNode;
  cols?: Columns;
  rows?: Rows;
  className?: string;
  title?: string;
  subtitle?: string;
  subtitleHighlight?: SubtitleHighlight;
  icon?: keyof typeof icons;
  iconTopCorner?: keyof typeof icons;
  iconImageUrl?: string;
  tabs?: CardTab[];
  slideIn?: boolean;
  mountWhenInView?: boolean;
  twTabsContainer?: string;
  showMouseGlow?: boolean;
  isVideoCard?: boolean;
}

export interface CardContainer {
  cols?: Columns;
  rows?: Rows;
}

export const cardPadding = {
  x: 'px-[15px] @md:px-[25px] @lg:px-[25px] @xl:px-[30px] md:px-[30px]',
  y: 'py-[15px] @md:py-[25px] @lg:py-[25px] @xl:py-[30px] md:py-[30px]',
  all: 'p-[15px] @md:p-[25px] @lg:p-[25px] @xl:p-[30px] md:p-[30px]',
};

const Card: FC<CardProps> = ({
  children = undefined,
  cols = 6,
  rows = 1,
  className = '',
  title = undefined,
  subtitle = undefined,
  subtitleHighlight = undefined,
  icon = undefined,
  iconTopCorner = undefined,
  iconImageUrl = undefined,
  tabs = undefined,
  slideIn = true,
  mountWhenInView = true,
  twTabsContainer = '',
  isVideoCard = false,
}) => {
  const colSpan = getColSpan(cols);
  const rowSpan = getRowSpan(rows);

  const cardRef = useRef<HTMLDivElement>(null);
  const [animateIn, setAnimateIn] = useState<boolean>(!mountWhenInView);
  const [showIconAnimation, setShowIconAnimation] = useState<boolean>(true);
  const { inView } = useIntersectionObserver(cardRef.current, {
    threshold: 0.6,
  });

  useEffect(() => {
    if (!inView) return;
    if (inView && animateIn === false) setAnimateIn(true);
  }, [inView, animateIn]);

  useGSAP(() => {
    gsap.set(cardRef.current, { alpha: 0, y: slideIn ? 75 : 0 });
  }, []);

  useGSAP(() => {
    if (!animateIn) return;
    gsap.to(cardRef.current, {
      duration: 0.3 + Math.random() * 0.2,
      delay: 0.07 + Math.random() * 0.2,
      autoAlpha: 1,
      y: 0,
      ease: 'power1.out',
    });
  }, [animateIn]);

  const handleIconAnimationComplete = () => setShowIconAnimation(false);

  return (
    <section
      ref={cardRef}
      className={cn(
        '@container grid relative bg-white/5 rounded-[8px] w-full h-auto min-h-[160px] backdrop-blur-lg',
        colSpan,
        rowSpan,
        className
      )}
    >
      {animateIn && (
        <Tabs defaultValue={tabs ? tabs[0].title : ''}>
          <CardTitleBar isVideoCard={isVideoCard}>
            {title ? (
              <CardTitle
                title={title}
                subtitle={subtitle}
                subtitleHighlight={subtitleHighlight}
                icon={icon}
                iconImageUrl={iconImageUrl}
                showIconAnimation={showIconAnimation}
                onIconAnimationComplete={handleIconAnimationComplete}
              />
            ) : (
              <div />
            )}
            {tabs && tabs.length > 1 ? (
              <>
                {/* card width > @lg */}
                <div className="hidden @lg:grid relative w-full h-full justify-end items-start">
                  <TabsButtons
                    tabs={tabs}
                    handleTabClick={() => setShowIconAnimation(true)}
                  />
                </div>
                {/* card width < @lg */}
                <div className="block @lg:hidden absolute w-full h-auto top-[85px] @md:top-[95px] text-center">
                  <TabsButtons
                    tabs={tabs}
                    handleTabClick={() => setShowIconAnimation(true)}
                  />
                </div>
              </>
            ) : null}
          </CardTitleBar>
          <div />
          {tabs ? (
            <TabsContents tabs={tabs} twTabsContainer={twTabsContainer} />
          ) : (
            children
          )}
          {iconTopCorner && (
            <BPIcon
              icon={iconTopCorner}
              className="block absolute top-[16px] right-[16px] fill-white"
            />
          )}
        </Tabs>
      )}
    </section>
  );
};

export default Card;
